import {type ARIAFilterSuggestion, FilterValueType} from '@github-ui/filter-core'
import {getDefaultValueVisualDescriptors, getFilterValue, isIndexedFilterBlock} from '@github-ui/filter-core'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {ActionList} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback} from 'react'

import {useFilterQuery, useInput, useSuggestions} from '../context'
import {type ResolvedValueVisual, resolveValueVisualDescriptor} from '../utils/value-visuals'
import styles from './SuggestionItem.module.css'

type SuggestionItemProps = {
  /** The index of the suggestion item, used for identifying which is active */
  suggestionId: number
  /** Whether or not the item should be displayed as active */
  active?: boolean
} & ARIAFilterSuggestion

export const SuggestionItem = (suggestion: SuggestionItemProps) => {
  const {suggestionId, active, value, type, description, displayName, displayNameHtml, inlineDescription, ariaLabel} =
    suggestion
  const {suggestionSelected, activeSuggestionRef} = useSuggestions()
  const {inputRef} = useInput()
  const {filterProviders, filterQuery} = useFilterQuery()

  const provider = (() => {
    if (type === FilterValueType.Key) {
      const rawValue = getFilterValue(value) ?? ''
      const providerKey = rawValue.startsWith('-') ? rawValue.slice(1) : rawValue
      return filterProviders[providerKey]
    }

    if (type === 'keyword') return undefined

    const activeBlock = filterQuery.activeBlock
    return activeBlock && isIndexedFilterBlock(activeBlock) ? activeBlock.provider : undefined
  })()

  const renderValueVisual = type === FilterValueType.Key ? undefined : provider?.options.renderValueVisual
  const customLeadingVisual = renderValueVisual ? (renderValueVisual(suggestion) ?? null) : null
  const leadingVisualDescriptors = renderValueVisual
    ? customLeadingVisual
    : getDefaultValueVisualDescriptors(suggestion, provider?.icon)
  const resolvedLeadingVisual = resolveValueVisualDescriptor(leadingVisualDescriptors, provider?.icon)

  const onSuggestionSelectHandler = useCallback(() => {
    suggestionSelected(suggestion)
    inputRef.current?.focus()
  }, [inputRef, suggestion, suggestionSelected])

  const setActiveSuggestionRef = useCallback(
    (node: HTMLLIElement) => {
      if (active && node) {
        activeSuggestionRef.current = node
        node.scrollIntoView({block: 'nearest'})
      } else {
        activeSuggestionRef.current = null
      }
    },
    [active, activeSuggestionRef],
  )

  const displayNameClass = clsx(styles.displayName, description ? styles.boldText : styles.normalText)

  const renderSuggestionVisual = (visual: ResolvedValueVisual) => {
    if (visual.kind === 'avatar') {
      return (
        <GitHubAvatar
          src={visual.url}
          alt={visual.alt}
          square={visual.square ?? false}
          className={styles.GitHubAvatar_0}
        />
      )
    }

    if (visual.kind === 'color-swatch') {
      return <div style={{backgroundColor: visual.color}} className={styles.Box_1} />
    }

    const cssIconColor = visual.color ?? 'currentcolor'
    const VisualIcon = visual.icon
    return <VisualIcon fill={cssIconColor} />
  }

  return (
    <ActionList.Item
      id={`suggestion-${suggestionId}`}
      onSelect={onSuggestionSelectHandler}
      active={active}
      role="option"
      aria-label={ariaLabel}
      aria-selected={active}
      tabIndex={-1}
      aria-labelledby={undefined}
      ref={setActiveSuggestionRef}
      className={styles.item}
    >
      {resolvedLeadingVisual ? (
        <ActionList.LeadingVisual>
          <div className={styles.Box_0}>{renderSuggestionVisual(resolvedLeadingVisual)}</div>
        </ActionList.LeadingVisual>
      ) : null}
      {displayNameHtml !== undefined ? (
        <VerifiedHTMLText html={displayNameHtml} className={displayNameClass} />
      ) : (
        <span className={displayNameClass}>{displayName ?? getFilterValue(value) ?? ''}</span>
      )}
      {description && inlineDescription !== undefined && (
        <ActionList.Description
          variant={inlineDescription ? 'inline' : 'block'}
          className={styles.ActionList_Description}
          truncate={!!inlineDescription}
        >
          {description}
        </ActionList.Description>
      )}
    </ActionList.Item>
  )
}
